/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  ThemeProvider,
  Autocomplete,
  MenuItem,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Typography
} from '@mui/material';
import { Formik } from 'formik';
import Grid2 from '@mui/material/Grid2';
import { getUsersByClinicId } from '../../services/apiService';
import { Clinician, Role, ROLES } from '../Surgery/oocyteRetrieval/OocyteRetrival.helper';
import { useAlert } from '../alertProvider';
import { useLoader } from '../loaderProvider/loader';
import { useClinic } from '../ClinicProvider';
import { embryoList, getPgtOrders, initialPgtOrder, PgtOrderValidationSchema, storeBiopsy, transferTypeOptions } from './pgtOrder.helper';
import { RequiredLabel } from '../../utils/commonUtils';
import { fetchViaProxy } from './pgtaHelper';
import { usePatientContext } from '../patinetProvider';
interface PgtOrderFormProps {
  onCancel: () => void;
}

const PgtOrderForm: React.FC<PgtOrderFormProps> = ({ onCancel }) => {


  const { addAlert } = useAlert();
  const { showLoader, hideLoader } = useLoader();
  const { ClinicId } = useClinic();
  const [barcodes, setBarcodes] = useState<string[]>([]);
  const [embryologists, setEmbryologists] = useState<{ id: string; name: string }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [physicians, setPhysicians] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [orders, setOrders] = useState<any[]>([]);
  const { patientId } = usePatientContext();

  const fetchBarcodes = useCallback(async () => {
    try {
      const response = await fetchViaProxy('/api/v2/barcodes', 'GET');      
      const rawData = response?.data;
      // Handle unexpected or malformed data
      if (!Array.isArray(rawData)) {
        console.warn('📛 Invalid barcode data structure: Not an array', rawData);
      }

      // Handle nested array case (e.g., [[...barcodes]])
      const barcodes = Array.isArray(rawData[0]) ? rawData[0] : rawData;
      if (!barcodes.length) {
        console.info('ℹBarcode list is empty');
      }
      console.log('✅ Parsed Barcodes:', barcodes);
      setBarcodes(barcodes);
    } catch (error) {
      console.error('Failed to fetch barcodes:', error);
    }
  }, []);

  const fetchEmbryologists = useCallback(async () => {
    try {
      const response = await fetchViaProxy('/api/v2/embryologists', 'GET');
      const rawData = response?.data;

      if (!Array.isArray(rawData) || typeof rawData[0] !== 'object') {
        console.warn('Unexpected structure for embryologists:', rawData);
        return;
      }

      const listObj = rawData[0]; // Correct: first (and only) object
      const parsedList = Object.entries(listObj).map(([id, name]) => ({
        id,
        name: String(name).trim(),
      }));

      console.log(' Parsed Embryologists:', parsedList);
      setEmbryologists(parsedList);
    } catch (error) {
      console.error(' Failed to fetch embryologists:', error);
    }
  }, []);

  const fetchPhysicians = useCallback(async () => {
    try {
      const response = await fetchViaProxy('/api/v2/doctors', 'GET');
      const rawData = response?.data?.[0] || {};

      // Transform the object into an array
      const parsed = Object.entries(rawData).map(([id, name]) => ({
        id,
        name: String(name).trim(),
      }));

      setPhysicians(parsed); //  Now this is an array usable in Autocomplete
      console.log(' Fetched Physicians:', parsed);
      
    } catch (error) {
      console.error('Failed to fetch doctors:', error);
    }
  }, []);

  useEffect(() => {
    fetchBarcodes();
    fetchEmbryologists();
    fetchPhysicians();
  }, [fetchBarcodes, fetchEmbryologists, fetchPhysicians]); 

  useEffect(() => {
    if (patientId) {
      getPgtOrders(patientId, 'PROGENESIS').then((response) => {
        setOrders(response.objects || []);
      });
    }
  }, [patientId]);

  // Sanitize function to format payload before submission
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sanitizePgtOrderValues = (values: any, allEmbryos: any[]) => {
    const selectedEmbryosSet = new Set(values.selectedEmbryos);
    console.log('Sanitizing PGT Order Values:', values, 'Selected Embryos Set:', selectedEmbryosSet);

    const embryos = allEmbryos
      .filter((row) => selectedEmbryosSet.has(row.id))
      .map((row) => {
        console.log('Mapping Embryo Row:', row);  //  log here
        return {
          embryo_id: row.id,
          number_cells: row.number_cells,
          biopsy_type: 'day ' + row.day,
          performed_by: row.performed_by,
          biopsy_date: values.date,
          embryo_grade: row.grade,
          tubing_by: row.tubing_by,
          witness_by: row.witness_by,
          biopsy_retrieval_date: values.date,
          pn_status: row.pn == null ? '' : row.pn,
          culture: row.action,
        };
      });


    return {
      ...values,
      embryos,
    };
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePgtSubmit = (values:any) => {
    console.log('Submitted PGT Order Values:', values);

    const payload = sanitizePgtOrderValues(values, orders);
    console.log('Sanitized Payload:', payload);

    // Build final payload for API
    const apiPayload = {
      patient: patientId,
      clinic_id: ClinicId,
      barcode: payload.barcode,
      biopsy_date: payload.date,
      performed_by: payload.physician,
      transfer_type: payload.transfer_type,
      embryos: payload.embryos,
      
      biopsy_retrieval_date: payload.date,
      test: 1,
    };

    showLoader();
    storeBiopsy(apiPayload, ClinicId)
      .then((response) => {
        hideLoader();
        // addAlert({ type: 'success', message: 'PGT order submitted successfully!' });
        console.log('PGT Order API response:', response);
        onCancel();
      })
      .catch((error) => {
        hideLoader();
        // addAlert({ type: 'error', message: 'Failed to submit PGT order.' });
        console.error('PGT Order API error:', error);
      });
  };


  return (
    <Formik
      initialValues={initialPgtOrder}
      validationSchema={PgtOrderValidationSchema}
      enableReinitialize
      onSubmit={handlePgtSubmit}
    >
      {({         
        values,
        setFieldValue,
        submitForm,
        validateForm,
        errors,
        dirty,  handleBlur, handleChange, handleSubmit, isSubmitting, touched, resetForm, setFieldTouched }) => (
        <form onSubmit={handleSubmit}>
          <Card sx={{ p: 1, boxShadow: 'none', border: 'none' }}>
            <CardContent>
              <Grid2 container spacing={2} justifyContent="start" sx={{ marginTop: 2 , marginRight:2}}>
                <Grid2 size={{ xs: 6, sm:4, md: 4, lg:3 }}>
                  <TextField
                    fullWidth
                    size="small" 
                    label={RequiredLabel('Date', true)}
                    name="date"
                    type="date"
                    InputLabelProps={{shrink: true}}
                    value={values.date}
                    onBlur={handleBlur}
                    onChange={(e) => setFieldValue('date', e.target.value || null)}
                    error={touched.date && Boolean(errors.date)}
                    // helperText={touched.date && errors.date}
                    aria-label="Date Input Field"
                  />
                </Grid2>

                <Grid2 size={{ xs: 6, sm:4, md: 4, lg:3 }}>
                  <TextField
                    fullWidth
                    size="small" 
                    label={RequiredLabel('Time', true)}
                    name="time"
                    type="time"
                    InputLabelProps={{shrink: true}}
                    value={values.time}
                    onBlur={handleBlur}
                    onChange={(e) => setFieldValue('time', e.target.value || null)}
                    error={touched.time && Boolean(errors.time)}
                    // helperText={touched.time && errors.time}
                    aria-label="Time Input Field"
                  />
                </Grid2>

                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="barcode"
                    options={barcodes}
                    getOptionLabel={(option) => option}
                    value={values.barcode || null}
                    onChange={(_, value) => {
                      setFieldValue('barcode', value || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Barcode"
                        error={touched.barcode && Boolean(errors.barcode)}
                        // helperText={touched.barcode && errors.barcode  }
                      />
                    )}
                  />
                </Grid2>                
                
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="physician"
                    options={physicians}
                    getOptionLabel={(option) => option.name}
                    value={physicians.find((p) => p.id === values.physician) || null}
                    onChange={(_, value) => setFieldValue('physician', value?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Physician"
                        error={touched.physician && Boolean(errors.physician)}
                        // helperText={touched.physician && errors.physician}
                      />
                    )}
                  />
                </Grid2>
                
                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="embryologist"
                    options={embryologists}
                    getOptionLabel={(option) => option.name}
                    value={values.embryologist}
                    onChange={(_, value) => setFieldValue('embryologist', value)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Embryologist"
                        error={touched.embryologist && Boolean(errors.embryologist)}
                        
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    id="transfer_type"
                    options={transferTypeOptions}
                    getOptionLabel={(option) => option.label}
                    value={
                      transferTypeOptions.find((opt) => opt.value === values.transfer_type) || null
                    }
                    onChange={(_, selectedOption) => {
                      setFieldValue('transfer_type', selectedOption?.value || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Transfer Type"
                        error={touched.transfer_type && Boolean(errors.transfer_type)}
                        // helperText={touched.transfer_type && errors.transfer_type}
                      />
                    )}
                  />
                </Grid2>
              </Grid2>

              <Box mt={3}>
                <Table sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: '#f5f5f5', // solid light gray
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }} />
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>Embryo No.</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>Grade</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>Day</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>Stage</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#333' }}>Plan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((row) => {
                      const isSelected = values.selectedEmbryos?.includes(row.id);

                      return (
                        <TableRow
                          key={row.id}
                          hover
                          selected={isSelected}
                          sx={{
                            backgroundColor: isSelected
                              ? 'rgba(246, 94, 57, 0.1)'
                              : 'inherit',
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                            },
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              value={row.id}
                              checked={isSelected}
                              onChange={(_, checked) => {
                                const selected = new Set(values.selectedEmbryos || []);
                                if (checked) {
                                  selected.add(row.id);
                                } else {
                                  selected.delete(row.id);
                                }
                                setFieldValue('selectedEmbryos', Array.from(selected));
                              }}
                              sx={{
                                color: '#aaa',
                                '&.Mui-checked': {
                                  color: '#f65e39',
                                },
                                ...(touched.selectedEmbryos &&
                                  Boolean(errors.selectedEmbryos) &&
                                  !isSelected && {
                                  border: '1px solid red',
                                  borderRadius: '4px',
                                }),
                              }}
                            />
                          </TableCell>

                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.grade}</TableCell>
                          <TableCell>{row.day}</TableCell>
                          <TableCell>{row.stage || '-'}</TableCell>
                          <TableCell>{row.action}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>

                </Table>
              </Box>
              <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                <Button variant="outlined" onClick={onCancel}>Cancel</Button>
                <Button variant="contained" type="submit">Schedule Biopsy</Button>
              </Box>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
};

export default PgtOrderForm;